import { Box, Center, HStack, Icon, Image, Text, VStack } from '@chakra-ui/react';
import { FaPlus } from '@react-icons/all-files/fa/FaPlus';
import { ReactNode } from 'react';
import { MdOutlinePhotoCamera, MdPhotoCamera } from 'react-icons/md';

import { Button } from '../../../../../components/ui/buttons/Button';
import MarketplaceCard from '../MarketplaceCard';
import { PHOTOS_LIVE_VIEW_THRESHOLD, PHOTO_SLOT_COUNT } from '../constants';

type PhotoPreview = {
  id: string;
  url: string | null;
};

export type PhotosCardVariant = 'live' | 'almostLive' | 'postAcceptance';

type MarketplaceLivePhotosCardProps = {
  photosUploaded: number;
  photos: PhotoPreview[];
  onAddPhotos: () => void;
  variant?: PhotosCardVariant;
  onUploadDocument?: () => void;
};

const PHOTOS_GRADIENT =
  'radial-gradient(circle at 90% -10%, var(--chakra-colors-yellowGlow), transparent 55%), linear-gradient(180deg, var(--chakra-colors-ivoryGlow) 0%, var(--chakra-colors-white) 70%)';

const SINGLE_PHOTO_COPY =
  'Great start. A few more good photos help buyers bid with confidence, and your listing stays live the whole time.';

const ALMOST_LIVE_SINGLE_PHOTO_COPY =
  'Great start. A few more good photos help buyers bid with confidence the moment your listing goes live.';

const AT_THRESHOLD_COPY: Record<PhotosCardVariant, string> = {
  live: 'Looking great. Buyers can see your vehicle in full. Add or replace photos anytime.',
  almostLive:
    'Looking great. Buyers will see your vehicle in full the moment your listing goes live. Add or replace photos anytime.',
  postAcceptance: 'Your photos are all set. Add or replace them anytime.',
};

const CardShell = ({
  title,
  withGradient,
  children,
}: {
  title: string;
  withGradient: boolean;
  children: ReactNode;
}) => (
  <MarketplaceCard
    header={title}
    headerBorderColor="yellowLight"
    borderColor="leaseEndYellow"
    background={withGradient ? PHOTOS_GRADIENT : 'white'}
  >
    {children}
  </MarketplaceCard>
);

const ZERO_PHOTO_COPY: Record<PhotosCardVariant, { heading: ReactNode; body: string }> = {
  live: {
    heading: "Add photos so buyers know what they're bidding on.",
    body: 'A few good photos help buyers bid with confidence. It only takes a few minutes, and your listing stays live the whole time.',
  },
  almostLive: {
    heading: (
      <>
        <Box as="span" display={{ base: 'none', md: 'inline' }}>
          Add photos so buyers know what they're bidding on
        </Box>
        <Box as="span" display={{ base: 'inline', md: 'none' }}>
          Add photos while you wait
        </Box>
      </>
    ),
    body: 'Add them while you wait, so your listing is ready the moment it goes live.',
  },
  postAcceptance: {
    heading: 'Add photos of your vehicle.',
    body: 'You can still add or update your vehicle photos here anytime.',
  },
};

const MarketplaceLivePhotosCard = ({
  photosUploaded,
  photos,
  onAddPhotos,
  variant = 'live',
  onUploadDocument,
}: MarketplaceLivePhotosCardProps) => {
  const isPostAcceptance = variant === 'postAcceptance';
  const cardTitle = isPostAcceptance ? 'Your photos' : 'Listing photos';
  const documentRow =
    !isPostAcceptance && onUploadDocument ? (
      <Box
        borderTopWidth="1px"
        borderTopColor="yellowLight"
        px={{ base: '18px', md: '24px' }}
        py="14px"
      >
        <Text fontSize="13.5px" fontWeight="700" color="leaseEndBlue">
          Have a document for us?
        </Text>
        <Text fontSize="13px" color="charcoal" lineHeight="1.5" mb={1}>
          Like your payoff statement from your lender.
        </Text>
        <Button
          onClick={onUploadDocument}
          trackClick="marketplace_live_upload_document"
          variant="link"
          color="oceanBoatBlue"
          fontWeight="700"
          fontSize="13px"
        >
          Upload a document
        </Button>
      </Box>
    ) : null;
  if (photosUploaded === 0) {
    return (
      <CardShell title={cardTitle} withGradient>
        <VStack spacing={0} align="stretch" px={{ base: '18px', md: '24px' }} py="22px">
          <Center
            w="64px"
            h="64px"
            borderRadius="16px"
            bg="leaseEndYellow"
            color="leaseEndBlue"
            mb={4}
          >
            <Icon as={MdOutlinePhotoCamera} boxSize="36px" />
          </Center>
          <Text
            fontSize={{ base: '18px', md: '22px' }}
            fontWeight="700"
            color="leaseEndBlue"
            lineHeight="1.2"
            mb={2}
          >
            {ZERO_PHOTO_COPY[variant].heading}
          </Text>
          <Text fontSize="13.5px" color="charcoal" lineHeight="1.55" mb={4} maxW="36ch">
            {ZERO_PHOTO_COPY[variant].body}
          </Text>
          <Button
            onClick={onAddPhotos}
            trackClick="marketplace_live_photos_add"
            w="100%"
            bg="leaseEndYellow"
            color="leaseEndBlue"
            borderRadius="full"
            fontWeight="800"
            letterSpacing="0.04em"
            textTransform="uppercase"
            fontSize="13px"
            py="14px"
            leftIcon={<Icon as={MdPhotoCamera} boxSize="14px" />}
            _hover={{ bg: 'yellowLight' }}
          >
            Add photos
          </Button>
        </VStack>
        {documentRow}
      </CardShell>
    );
  }

  const heroPhoto = photos.find((photo) => photo.url != null)?.url ?? null;
  const thumbnails = photos.filter((photo) => photo.url != null).slice(1);
  const atThreshold = photosUploaded >= PHOTOS_LIVE_VIEW_THRESHOLD;
  const isSinglePhoto = photosUploaded === 1;

  const getBelowThresholdCopy = () => {
    if (isPostAcceptance) {
      return `${photosUploaded} of ${PHOTO_SLOT_COUNT} added. Add or replace photos anytime.`;
    }
    if (isSinglePhoto) {
      return variant === 'almostLive' ? ALMOST_LIVE_SINGLE_PHOTO_COPY : SINGLE_PHOTO_COPY;
    }
    return `${photosUploaded} of ${PHOTO_SLOT_COUNT} added. Add a few more to round out your listing.`;
  };

  return (
    <CardShell title={cardTitle} withGradient={!atThreshold}>
      <VStack spacing={3} align="stretch" px={{ base: '18px', md: '24px' }} py="22px">
        <Box
          position="relative"
          aspectRatio="2 / 1"
          borderRadius="10px"
          overflow="hidden"
          bg="grayLightest"
        >
          {heroPhoto != null && (
            <Image src={heroPhoto} alt="Listing photo" w="100%" h="100%" objectFit="cover" />
          )}
          <Box
            position="absolute"
            bottom="10px"
            right="12px"
            bg="blackAlpha.600"
            color="white"
            fontSize="10px"
            fontWeight="800"
            letterSpacing="0.08em"
            textTransform="uppercase"
            borderRadius="full"
            px={2}
            py={1}
          >
            {photosUploaded} of {PHOTO_SLOT_COUNT}
          </Box>
        </Box>

        <HStack
          spacing={1.5}
          align="stretch"
          overflowX="auto"
          minW={0}
          pb={1}
          sx={{
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { height: '6px' },
            '&::-webkit-scrollbar-thumb': {
              background: 'var(--chakra-colors-grayMediumBackground)',
              borderRadius: '3px',
            },
          }}
        >
          {thumbnails.map((photo) => (
            <Box
              key={photo.id}
              flexShrink={0}
              w={{ base: '64px', md: '76px' }}
              aspectRatio="2 / 1"
              borderRadius="6px"
              overflow="hidden"
            >
              <Image src={photo.url ?? ''} alt="" w="100%" h="100%" objectFit="cover" />
            </Box>
          ))}
          {!atThreshold && (
            <Center
              flexShrink={0}
              w={{ base: '64px', md: '76px' }}
              aspectRatio="2 / 1"
              borderRadius="6px"
              borderWidth="1.5px"
              borderStyle="dashed"
              borderColor="leaseEndYellow"
              bg="ivoryWarm"
              cursor="pointer"
              onClick={onAddPhotos}
            >
              <Icon as={FaPlus} boxSize="14px" color="leaseEndYellow" />
            </Center>
          )}
        </HStack>

        {atThreshold ? (
          <>
            <Text fontSize="13px" color="charcoal" lineHeight="1.5">
              {AT_THRESHOLD_COPY[variant]}
            </Text>
            <Button
              onClick={onAddPhotos}
              trackClick="marketplace_live_photos_manage"
              variant="link"
              alignSelf="flex-start"
              color="oceanBoatBlue"
              fontWeight="700"
              fontSize="13px"
              leftIcon={<Icon as={MdPhotoCamera} boxSize="13px" />}
            >
              Manage photos
            </Button>
          </>
        ) : (
          <>
            <Text fontSize={isSinglePhoto ? '15px' : '13px'} color="charcoal" lineHeight="1.5">
              {getBelowThresholdCopy()}
            </Text>
            <Button
              onClick={onAddPhotos}
              trackClick="marketplace_live_photos_add_more"
              w="100%"
              bg="leaseEndYellow"
              color="leaseEndBlue"
              borderRadius="full"
              fontWeight="800"
              letterSpacing="0.04em"
              textTransform="uppercase"
              fontSize="13px"
              py="14px"
              leftIcon={<Icon as={MdPhotoCamera} boxSize="14px" />}
              _hover={{ bg: 'yellowLight' }}
            >
              Add photos
            </Button>
          </>
        )}
      </VStack>
      {documentRow}
    </CardShell>
  );
};

export default MarketplaceLivePhotosCard;
